<script lang="ts" context="module">
  // Module-level dispatcher list. onMount does NOT fire in the jsdom test
  // setup, so we wire the document listener at module init (guarded by a
  // document check for SSR safety) and fan the event out to every mounted
  // instance. In practice there's a single <Toast /> mounted in App.svelte.
  type Level = 'success' | 'error' | 'info';
  interface ToastDetail { message: string; level: Level }
  type Handler = (d: ToastDetail) => void;

  const handlers = new Set<Handler>();

  if (typeof document !== 'undefined') {
    document.addEventListener('app:toast', (e: Event) => {
      const detail = (e as CustomEvent<ToastDetail>).detail;
      if (!detail || !detail.message) return;
      const level: Level = detail.level ?? 'info';
      for (const h of handlers) h({ message: detail.message, level });
    });
  }
</script>

<script lang="ts">
  import { onDestroy } from 'svelte';

  interface ToastItem { id: number; message: string; level: Level }

  let toasts: ToastItem[] = [];
  let nextId = 1;
  const timers = new Map<number, ReturnType<typeof setTimeout>>();
  const DISMISS_MS = 2500;

  function dismiss(id: number) {
    toasts = toasts.filter((t) => t.id !== id);
    const timer = timers.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.delete(id);
    }
  }

  // Exported so tests (and callers) can push a toast without going through
  // the document event if they prefer.
  export function handleToast(detail: ToastDetail) {
    const id = nextId++;
    toasts = [...toasts, { id, message: detail.message, level: detail.level }];
    timers.set(
      id,
      setTimeout(() => dismiss(id), DISMISS_MS),
    );
  }

  handlers.add(handleToast);

  onDestroy(() => {
    handlers.delete(handleToast);
    for (const t of timers.values()) clearTimeout(t);
    timers.clear();
  });
</script>

<div class="toast-stack" aria-live="polite">
  {#each toasts as t (t.id)}
    <button
      type="button"
      class="toast toast-{t.level}"
      role={t.level === 'error' ? 'alert' : 'status'}
      on:click={() => dismiss(t.id)}
      aria-label="Dismiss notification"
    >
      <span class="toast-icon" aria-hidden="true">
        {#if t.level === 'success'}✓{:else if t.level === 'error'}✕{:else}ℹ{/if}
      </span>
      <span class="toast-msg">{t.message}</span>
    </button>
  {/each}
</div>

<style>
  .toast-stack {
    position: fixed;
    top: 1rem;
    left: 50%;
    transform: translateX(-50%);
    z-index: 2000;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    align-items: center;
    pointer-events: none;
    width: max-content;
    max-width: min(92vw, 28rem);
  }
  .toast {
    pointer-events: auto;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.6rem 0.9rem;
    border-radius: 0.6rem;
    border: 1px solid transparent;
    font-family: var(--font-body, system-ui, sans-serif);
    font-size: 0.92rem;
    font-weight: 500;
    line-height: 1.25;
    text-align: left;
    cursor: pointer;
    box-shadow: var(--shadow-mid, 0 6px 18px rgba(15, 33, 27, 0.18));
    animation: toast-in 0.18s ease-out;
    max-width: 100%;
  }
  .toast-icon {
    flex-shrink: 0;
    font-weight: 700;
  }
  .toast-msg {
    word-break: break-word;
  }
  .toast-success {
    background: var(--leaf, #1f5135);
    color: var(--paper, #f7f3ea);
    border-color: var(--leaf-bright, #2d7a4f);
  }
  .toast-error {
    background: var(--alert, #9b3340);
    color: var(--paper, #f7f3ea);
    border-color: var(--alert, #9b3340);
  }
  .toast-info {
    background: var(--paper-soft, #fcfaf4);
    color: var(--ink, #15211b);
    border-color: var(--rule-strong, #c9c0ac);
  }
  @keyframes toast-in {
    from { opacity: 0; transform: translateY(-6px); }
    to { opacity: 1; transform: translateY(0); }
  }
</style>
